import React from 'react';
import { Box, Button, Container, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';

/**
 * ErrorBoundary — Global error boundary to catch React component crashes.
 *
 * Prevents one broken component from crashing the entire app.
 * Shows user-friendly error message + retry button.
 *
 * Usage:
 *   <ErrorBoundary>
 *     <YourApp />
 *   </ErrorBoundary>
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onReset={this.handleReset}
        />
      );
    }

    return this.props.children;
  }
}

/**
 * ErrorFallback — Standalone error display component.
 * Shows error details in development; generic message in production.
 */
const ErrorFallback = ({ error, errorInfo, onReset }) => {
  const theme = useTheme();
  const isDev = process.env.NODE_ENV === 'development';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        background: theme.palette.background.default,
        padding: 2,
      }}
    >
      <Container maxWidth="sm">
        <Stack spacing={3} alignItems="center">
          <ErrorRoundedIcon
            sx={{ fontSize: 80, color: theme.palette.error.main, opacity: 0.7 }}
          />

          <Typography variant="h4" sx={{ fontWeight: 700, textAlign: 'center' }}>
            Something went wrong
          </Typography>

          <Typography
            variant="body1"
            sx={{
              textAlign: 'center',
              color: 'text.secondary',
              lineHeight: 1.6,
            }}
          >
            We encountered an unexpected error. Try refreshing the page or
            returning to the dashboard.
          </Typography>

          {isDev && error && (
            <Box
              sx={{
                width: '100%',
                background: theme.palette.error.lighter || '#fee',
                border: `1px solid ${theme.palette.error.main}`,
                borderRadius: 1,
                p: 2,
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                overflow: 'auto',
                maxHeight: 200,
                color: theme.palette.error.dark,
              }}
            >
              <Typography
                component="div"
                variant="caption"
                sx={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {error?.toString()}
                {'\n\n'}
                {errorInfo?.componentStack}
              </Typography>
            </Box>
          )}

          <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
            <Button
              variant="outlined"
              onClick={() => (window.location.href = '/')}
              sx={{ flex: 1 }}
            >
              Home
            </Button>
            <Button
              variant="contained"
              startIcon={<RefreshRoundedIcon />}
              onClick={onReset}
              sx={{ flex: 1 }}
            >
              Retry
            </Button>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
};

export default ErrorBoundary;
